/* eslint-disable react-hooks/exhaustive-deps */
// TODO: use localized components
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import { TableActionBar, TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import React, { useMemo, useState, useEffect } from 'react';
import { TableData } from '../../redux/types';
import { Debug } from '../../utils/debug';
import { DownloadCsv } from '../composites';
import { PopoverCell } from './PopoverCell';
import { StyledTable } from './Table';

export type RowKeyBuilderFunction<T> = (rowArray: string[], rowObject: T) => string;

export interface PaginatedTableProps<T> {
    children?: React.ReactNode;
    currentPage: number;
    data: TableData<T, keyof T>;
    dataTestId?: string;
    numberOfPages: number;
    onPageChange(value: string): void;
    onSort(sort: { sortColumn: string; sortDirection: string }): void;
    sortColumn?: string;
    sortDirection?: string;
    rowKey?: string | RowKeyBuilderFunction<T>;
    getAllData?: () => Promise<TableData<any, keyof any>>;
    dataName?: string;
    enableCheckboxes?: boolean;
    showCheckboxes?: boolean;
    onSelectionChange?: (selectedIds: string[]) => void;
    customButtons?: React.ReactNode;
    checkboxDisabledPredicate?: (rowData: T) => boolean;
    layout?: 'inline' | 'stacked';
    filterAlignment?: 'top' | 'center' | 'bottom';
    inlineFilterActions?: boolean;
    filterTags?: React.ReactNode;
    searchButton?: React.ReactNode;
    clearButton?: React.ReactNode;
    tableKey?: string;
    // How many logical columns one cell covers, or undefined for the default. A help column counts as one
    // here; its extra physical width is added in later.
    cellColumnSpan?: (rowData: T, columnIndex: number) => number | undefined;
    // Marks a row as a detail child of the row above. Opt in, so callers that skip it render as before.
    // Separate from cellColumnSpan because that sets width, not whether a row belongs to the line above.
    isChildRow?: (rowData: T) => boolean;
}

// eslint-disable-next-line max-lines-per-function
const PaginatedTable = <T extends any>({
    data,
    currentPage,
    numberOfPages,
    onPageChange,
    onSort,
    sortColumn,
    sortDirection,
    dataTestId,
    children,
    rowKey,
    getAllData,
    dataName,
    enableCheckboxes = false,
    showCheckboxes = false,
    onSelectionChange,
    customButtons,
    checkboxDisabledPredicate,
    layout = 'inline',
    filterAlignment = 'bottom',
    inlineFilterActions = false,
    filterTags,
    searchButton,
    clearButton,
    tableKey,
    cellColumnSpan,
    isChildRow,
}: PaginatedTableProps<T>) => {
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const rowKeyBuilder: RowKeyBuilderFunction<T> = useMemo<RowKeyBuilderFunction<T>>(() => {
        if (typeof rowKey === 'string') {
            return (rowArray: string[], rowObject: T) => {
                const key = (rowObject[rowKey as keyof T] as unknown) as string;
                Debug.assert(key !== undefined, 'Invalid row key. Key:', key, 'rowKey:', rowKey);
                return key;
            };
        } else if (typeof rowKey === 'function') {
            return rowKey as RowKeyBuilderFunction<T>;
        } else {
            return (rowArray: string[]) => {
                return rowArray[0];
            };
        }
    }, [rowKey]);

    const handleSelectAll = (checked: boolean) => {
        setSelectAll(checked);
        const allIds: string[] = [];
        if (checked) {
            data.mapRows((row, rowData) => {
                const isDisabled = checkboxDisabledPredicate ? checkboxDisabledPredicate(rowData) : false;
                if (!isDisabled) {
                    allIds.push(rowKeyBuilder(row, rowData));
                }
            });
            setSelectedRows(allIds);
        } else {
            setSelectedRows([]);
        }
    };

    const handleRowSelect = (rowId: string, checked: boolean) => {
        if (checked) {
            setSelectedRows([...selectedRows, rowId]);
        } else {
            setSelectedRows(selectedRows.filter((id) => id !== rowId));
        }
    };

    useEffect(() => {
        if (enableCheckboxes && onSelectionChange) {
            onSelectionChange(selectedRows);
        }
    }, [selectedRows]);

    useEffect(() => {
        setSelectedRows([]);
        setSelectAll(false);
    }, [currentPage]);

    useEffect(() => {
        if (!showCheckboxes) {
            setSelectedRows([]);
            setSelectAll(false);
        }
    }, [showCheckboxes]);

    const renderActionBar = () => {
        if (layout === 'stacked') {
            return (
                <TableActionBar data-testid={`${dataTestId}-TableActionBar`}>
                    <Column spacing={'medium'} width={'100%'}>
                        {inlineFilterActions ? (
                            <Row spacing={'small'} alignmentVertical={filterAlignment} widths={['fill', 'fit']}>
                                <Row spacing={'small'} wrap={'down'} alignmentVertical={filterAlignment}>
                                    {children}
                                    {searchButton}
                                    {clearButton}
                                </Row>
                                <Pagination
                                    currentPage={currentPage}
                                    numberOfPages={numberOfPages}
                                    onChange={onPageChange}
                                    showSkipArrows={true}
                                    data-testid={`${dataTestId}-Pagination`}
                                />
                            </Row>
                        ) : (
                            children && (
                                <Row
                                    spacing={'small'}
                                    wrap={'down'}
                                    alignmentVertical={filterAlignment}
                                    alignmentHorizontal={'center'}
                                >
                                    {children}
                                </Row>
                            )
                        )}
                        {filterTags}
                        {!inlineFilterActions && (
                            <Row spacing={'small'} alignmentVertical={'center'} alignmentHorizontal={'justify'}>
                                <Row spacing={'small'}>
                                    {searchButton}
                                    {clearButton}
                                </Row>
                                <Pagination
                                    currentPage={currentPage}
                                    numberOfPages={numberOfPages}
                                    onChange={onPageChange}
                                    showSkipArrows={true}
                                    data-testid={`${dataTestId}-Pagination`}
                                />
                            </Row>
                        )}
                    </Column>
                </TableActionBar>
            );
        }
        return (
            <TableActionBar data-testid={`${dataTestId}-TableActionBar`}>
                {children}
                <Pagination
                    currentPage={currentPage}
                    numberOfPages={numberOfPages}
                    onChange={onPageChange}
                    showSkipArrows={true}
                    data-testid={`${dataTestId}-Pagination`}
                />
                {customButtons}
                {getAllData && dataName && <DownloadCsv getAllData={getAllData} dataName={dataName} />}
            </TableActionBar>
        );
    };

    const renderHeaderRow = () => (
        <TableRow data-testid={`${dataTestId}-HeaderTableRow`}>
            {enableCheckboxes && showCheckboxes && (
                <TableCell highlight={true} width={50}>
                    <Checkbox checked={selectAll} onChange={handleSelectAll} />
                </TableCell>
            )}
            {data.getHeaders().map((header) => {
                if (React.isValidElement(header)) {
                    return (
                        <TableCell
                            sortColumn={header.uniqueId}
                            highlight={true}
                            data-testid={header.uniqueId}
                            key={header.uniqueId}
                        >
                            {header}
                        </TableCell>
                    );
                }
                return [
                    <TableCell
                        sortColumn={
                            header.sortable === false || !header.title || !header.title.toString().trim()
                                ? undefined
                                : header.uniqueId
                        }
                        highlight={true}
                        data-testid={header.uniqueId}
                        key={header.uniqueId}
                        width={header.help ? 1 : undefined}
                    >
                        {header.title}
                    </TableCell>,
                    header.help ? (
                        <TableCell
                            id={`help-${header.uniqueId}`}
                            key={`help-${header.uniqueId}`}
                            highlight={true}
                            css={{ width: 1000 }}
                        >
                            {header.help}
                        </TableCell>
                    ) : null,
                ].flat();
            })}
        </TableRow>
    );

    const defaultColumnSpan = (columnIndex: number) => (data.getHeaders()[columnIndex].help ? 2 : 1);

    // A help column occupies two physical columns, so sum the covered widths instead of counting columns.
    // Counting would leave the cell short by one for each help column it covers.
    const physicalColumnSpan = (startIndex: number, logicalColumnSpan: number) => {
        const lastColumn = Math.min(startIndex + logicalColumnSpan, data.getHeaders().length);
        let coveredSpan = 0;
        for (let columnIndex = startIndex; columnIndex < lastColumn; columnIndex += 1) {
            coveredSpan += defaultColumnSpan(columnIndex);
        }
        return coveredSpan;
    };

    // A cell with an explicit span covers the columns after it, and those columns render no cell of their
    // own. Without a span, every column renders its own cell as before.
    const cellsFor = (row: string[], rowData: T) => {
        const explicitSpans = row.map((_field, columnIndex) =>
            cellColumnSpan ? cellColumnSpan(rowData, columnIndex) : undefined
        );
        const isCoveredByEarlierCell = (columnIndex: number) =>
            explicitSpans.some(
                (span, spanningIndex) =>
                    span !== undefined && spanningIndex < columnIndex && spanningIndex + span > columnIndex
            );
        return row
            .map((field, columnIndex) => {
                const logicalColumnSpan = explicitSpans[columnIndex];
                return {
                    field: field,
                    columnIndex: columnIndex,
                    columnSpan:
                        logicalColumnSpan !== undefined
                            ? physicalColumnSpan(columnIndex, logicalColumnSpan)
                            : defaultColumnSpan(columnIndex),
                };
            })
            .filter((cell) => !isCoveredByEarlierCell(cell.columnIndex));
    };

    const renderDataRows = () =>
        data.mapRows((row, rowData) => {
            const key = rowKeyBuilder(row, rowData);
            const isSelected = selectedRows.includes(key);
            const isCheckboxDisabled = checkboxDisabledPredicate ? checkboxDisabledPredicate(rowData) : false;

            return (
                <TableRow
                    highlightOnHover={true}
                    key={key}
                    data-testid={`${dataTestId}-TableRow`}
                    data-child-row={isChildRow && isChildRow(rowData) ? 'true' : undefined}
                >
                    {enableCheckboxes && showCheckboxes && (
                        <TableCell width={50}>
                            <Checkbox
                                checked={isSelected}
                                onChange={(checked) => handleRowSelect(key, checked)}
                                disabled={isCheckboxDisabled}
                            />
                        </TableCell>
                    )}
                    {cellsFor(row, rowData).map(({ field, columnIndex, columnSpan }) => {
                        const cellKey = String(columnIndex);
                        if (React.isValidElement(field)) {
                            return (
                                <TableCell
                                    data-testid={`${dataTestId}-TableCell`}
                                    key={cellKey}
                                    columnSpan={columnSpan}
                                >
                                    {field}
                                </TableCell>
                            );
                        }
                        const header = data.getHeaders()[columnIndex];
                        const maxWidth = header ? header.maxWidth : undefined;
                        let fieldStringValue: string = typeof field === 'string' ? field : '';
                        if (Array.isArray(field)) {
                            fieldStringValue = field.join('; ');
                        }

                        if (maxWidth && fieldStringValue.length > maxWidth) {
                            return (
                                <TableCell
                                    data-testid={`${dataTestId}-TableCell`}
                                    key={cellKey}
                                    columnSpan={columnSpan}
                                >
                                    {fieldStringValue.substr(0, maxWidth)}
                                    <PopoverCell buttonLable={'…'}>
                                        <Column
                                            alignmentHorizontal={'left'}
                                            alignmentVertical={'top'}
                                            spacing={'xxsmall'}
                                        >
                                            {Array.isArray(field) &&
                                                field.map((item) => (
                                                    <Text type={'b200'} key={item}>
                                                        {item}
                                                    </Text>
                                                ))}
                                            {typeof field === 'string' && (
                                                <Text type={'b200'} key={cellKey}>
                                                    {fieldStringValue}
                                                </Text>
                                            )}
                                        </Column>
                                    </PopoverCell>
                                </TableCell>
                            );
                        }
                        return (
                            <TableCell data-testid={`${dataTestId}-TableCell`} key={cellKey} columnSpan={columnSpan}>
                                {fieldStringValue}
                            </TableCell>
                        );
                    })}
                </TableRow>
            );
        });

    return (
        <Column width={'100%'} spacing={'none'}>
            {renderActionBar()}
            <StyledTable
                key={tableKey}
                $hasCheckboxes={showCheckboxes}
                headerRows={1}
                sortColumn={sortColumn}
                sortDirection={sortDirection === 'ascending' ? 'ascending' : 'descending'}
                onSort={onSort}
                showDividers={true}
                data-testid={dataTestId}
                spacing={'small'}
            >
                {renderHeaderRow()}
                {renderDataRows()}
            </StyledTable>
        </Column>
    );
};

export { PaginatedTable };
